import React, { FormEvent, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { capitalize, isEmpty } from 'lodash';

import { createBequestOptions } from 'modules/asset/helpers/labelSelectionHelper';
import { assetService } from 'modules/estatePlan/services';

import { Col, Row } from 'modules/bootstrap/components';
import { Button, SelectController } from 'modules/common/components';

import { ASSET_CATEGORIES } from 'modules/estatePlan/constants';

type Inputs = {
  id?: string;
  assetCategory?: SelectOption;
  userAsset: SelectOption;
};

interface BequestAssetFormProps {
  assetItem?: any;
  onCancel: () => void;
  onSuccess: (newBequest: any) => void;
}

function BequestAssetForm(props: BequestAssetFormProps) {
  const { onCancel, onSuccess } = props;
  const assetItem = (props.assetItem || {}) as any;

  const { t } = useTranslation([
    'auth',
    'common',
    'contacts',
    'will',
    'dashboard',
  ]);
  const {
    register,
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
    setError,

    formState: { errors },
  } = useForm<Inputs>();
  watch('assetCategory');

  const assetsData: AssetDTO[] =
    useSelector((state: any) => state.assets) || [];
  const categorySelected = getValues('assetCategory');

  const [formattedAssetsData, setFormattedAssetsData] =
    useState<AssetDTO[]>(assetsData);

  useEffect(() => {
    const getAssetsWithAllFormattedProviders = async () => {
      const assetsDataCopy = { ...assetsData };
      await Promise.all(
        Object.keys(assetsData)
          .filter((category) => assetsData[category].length)
          .map(async (category) => {
            const formattedCategorizedAssets = await Promise.all(
              assetsData[category].map(async (asset) => {
                if (asset.provider) {
                  const formattedProvider = await assetService.getProviderByKey(
                    asset.assetTypeName,
                    asset.provider
                  );
                  if (formattedProvider)
                    return { ...asset, provider: formattedProvider.value };
                }
                return asset;
              })
            );
            assetsDataCopy[category] = formattedCategorizedAssets;
          })
      );
      setFormattedAssetsData(assetsDataCopy);
    };

    getAssetsWithAllFormattedProviders();
  }, [assetsData]);

  const categoryOptions = Object.values(ASSET_CATEGORIES)
    .filter((item) => Array.isArray(assetsData[item]))
    .map((item) => ({
      value: item,
      label: capitalize(t(`dashboard:assets_${item}_tab`)),
    }));
  const defaultCategoryOptions = categoryOptions.find(
    (item) => item.value === assetItem.assetCategory
  );

  let assetsOptions = [] as SelectOption[];
  if (categorySelected) {
    assetsOptions = createBequestOptions(
      formattedAssetsData,
      categorySelected.value
    );
  }
  const defaultAssetsOptions = assetsOptions.find(
    (item) => item.value === assetItem.id
  );

  const selectPlaceholder = t('common:default_select_placeholder');

  React.useEffect(() => {
    setValue('userAsset', { label: selectPlaceholder, value: '' });
  }, [categorySelected]);

  const isNewItem = !assetItem.id;

  const onSubmit = async (formData: Inputs) => {
    const submitData = {
      ...formData,
    } as any;

    onSuccess(submitData);
    onCancel();
  };

  const textFieldRequired = t('common:field_required');

  const submit = (e: FormEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleSubmit(() => {
      const { userAsset } = getValues();
      if (isEmpty(userAsset) || userAsset.label === selectPlaceholder) {
        setError('userAsset', { type: 'manual', message: textFieldRequired });
      } else {
        onSubmit(getValues());
      }
    })();
  };

  return (
    <>
      <form onSubmit={submit} className="Form">
        {!isNewItem && (
          <input
            type="hidden"
            id="id"
            {...register('id')}
            value={assetItem.id}
          />
        )}

        <h1 className="Typography Typography--title u-pb-30 u-weight-600">
          {t('will:select_existing_asset')}
        </h1>

        <Row>
          <Col>
            <div className="Form-group">
              <label
                className={`Form-label ${
                  errors.assetCategory ? 'isErrored' : ''
                }`}
                htmlFor="subtype"
              >
                {t('will:asset_type_label')}
              </label>

              <SelectController
                id="assetCategory"
                name="assetCategory"
                options={categoryOptions}
                control={control}
                defaultValue={defaultCategoryOptions}
                className={`Select ${errors.assetCategory ? 'isErrored' : ''}`}
                rules={{ required: true }}
              />
              {errors.assetCategory && (
                <div className="Form-alert">
                  <span className="text-main-sm t-text-epsilon-600">
                    {textFieldRequired}
                  </span>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {categorySelected && (
          <Row>
            <Col>
              <div className="Form-group">
                <label
                  className={`Form-label ${
                    errors.userAsset ? 'isErrored' : ''
                  }`}
                  htmlFor="subtype"
                >
                  {t('will:asset_label')}
                </label>

                <SelectController
                  id="userAsset"
                  name="userAsset"
                  disabled
                  options={assetsOptions}
                  control={control}
                  defaultValue={defaultAssetsOptions}
                  className={`Select ${errors.userAsset ? 'isErrored' : ''}`}
                  rules={{ required: true }}
                />
                {errors.userAsset && (
                  <div className="Form-alert">
                    <span className="text-main-sm t-text-epsilon-600">
                      {textFieldRequired}
                    </span>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}

        <div className="t-flex t-flex-wrap t-justify-end">
          <Button
            category="secondary"
            className="t-whitespace-nowrap t-self-end"
            onClick={() => onCancel()}
          >
            {t('common:cancel')}
          </Button>
          <Button type="submit" className="t-ml-5 xs:t-mt-5">
            {t('common:save_and_continue')}
          </Button>
        </div>
      </form>
    </>
  );
}

export default BequestAssetForm;
